import React, { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";

const DOCS_VIDEOS = [
  {
    src: "/videos/Vera_Asistanı_ve_MCP_Araçları.mp4",
    poster: "/videos/vera-mcp.jpg",
    title: "Vera Asistanı ve MCP Araçları",
    desc: "MCP araçları ile Vera asistanınızı nasıl yapılandıracağınızı keşfedin.",
  },
  {
    src: "/videos/araçlar-dosyalar-iframe_1080p.mp4",
    poster: "/videos/araclar-dosyalar.jpg",
    title: "Araçlar, Dosyalar & Iframe",
    desc: "Dosya yükleme, araç kullanımı ve iframe entegrasyonlarını öğrenin.",
  },
  {
    src: "/videos/asistanlar_ve_entegrasyon.mp4",
    poster: "/videos/asistanlar-entegrasyon.jpg",
    title: "Asistanlar ve Entegrasyon",
    desc: "Asistan oluşturma ve harici servis entegrasyonlarının adımları.",
  },
  {
    src: "/videos/iş_akışı_ve_test_araması.mp4",
    poster: "/videos/is-akisi-test.jpg",
    title: "İş Akışı ve Test Araması",
    desc: "İş akışı tasarımı ve test arama özelliklerini yakından görün.",
  },
  {
    src: "/videos/raporlar-çağrı%20kayıtları_1080p.mp4",
    poster: "/videos/raporlar-kayitlar.jpg",
    title: "Raporlar & Çalıştırma Kayıtları",
    desc: "Detaylı raporlama ve çalıştırma kayıtlarının nasıl inceleneceğini görün.",
  },
  {
    src: "/videos/calistirma-planlari-takvim.mp4",
    poster: "/videos/calistirma-planlari.jpg",
    title: "Çalıştırma Planları & Takvim",
    desc: "Çalıştırma planlama ve takvim entegrasyonu ile planlarınızı yönetin.",
  },
];

const docsBase = (process.env.REACT_APP_DOCS_URL || "https://docs.verasist.ai").trim();

const VideoCard = ({ video, index }) => {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [imgError, setImgError] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  }, []);

  const handleClick = () => {
    const normalized =
      docsBase.startsWith("http://") || docsBase.startsWith("https://")
        ? docsBase
        : `https://${docsBase}`;
    window.open(`${normalized}/videos`, "_blank", "noopener");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative rounded-2xl overflow-hidden cursor-pointer"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        aspectRatio: "16 / 10",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Poster image — shown before video loads */}
      {!imgError && (
        <img
          src={video.poster}
          alt={video.title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      )}

      {/* Video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={video.src}
        poster={video.poster}
        muted
        loop
        playsInline
        preload="none"
      />

      {/* Overlay gradient — darker when not hovering */}
      <div
        className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
        style={{
          background:
            "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)",
          opacity: isHovering ? 0.6 : 1,
        }}
      />

      {/* Play icon — visible when not hovering */}
      <div
        className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none"
        style={{ opacity: isHovering ? 0 : 1 }}
      >
        <div
          className="flex items-center justify-center w-12 h-12 rounded-full"
          style={{
            background: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        </div>
      </div>

      {/* Label at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
        <h4
          className="text-sm font-semibold mb-1"
          style={{ color: "rgba(255,255,255,0.95)" }}
        >
          {video.title}
        </h4>
        <p
          className="text-xs leading-relaxed"
          style={{ color: "rgba(255,255,255,0.65)" }}
        >
          {video.desc}
        </p>
      </div>
    </motion.div>
  );
};

const DocsVideoCollageSection = () => {
  return (
    <section
      className="relative w-full py-24 px-6 lg:px-12"
      style={{ background: "transparent" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-3 px-3 py-1 rounded-full"
            style={{
              background: "rgba(99,102,241,0.12)",
              color: "var(--accent, #818cf8)",
            }}
          >
            Dökümantasyon
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold mb-4"
            style={{ color: "var(--fg)" }}
          >
            Her Şeyi Adım Adım Öğrenin
          </h2>
          <p
            className="text-base max-w-xl mx-auto"
            style={{ color: "var(--fg-muted, rgba(255,255,255,0.55))" }}
          >
            Vera platformunun tüm özelliklerini kısa videolarla keşfedin.
            Asistan kurulumundan raporlamaya kadar her konu elinizin altında.
          </p>
        </motion.div>

        {/* Video grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {DOCS_VIDEOS.map((video, i) => (
            <VideoCard key={video.src} video={video} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DocsVideoCollageSection;
